import { useRef, useState, type SubmitEvent } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import '../eldgard-waitlist.css';

// Public form endpoint, not an API key. Notifications go to the linked owner.
export const waitlistEndpoint = 'https://formspree.io/f/mqpaaodv';

export async function submitWaitlist(
  data: FormData,
  request: typeof fetch = fetch,
) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await request(waitlistEndpoint, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data,
      signal: controller.signal,
      credentials: 'omit',
      referrerPolicy: 'strict-origin-when-cross-origin',
    });
    if (response.status === 429) {
      throw new Error(
        'Too many requests right now. Please wait a little and try again.',
      );
    }
    if (!response.ok || response.redirected) {
      throw new Error(
        'Your signup could not be confirmed. Please try again or email hello@alxnsv.io.',
      );
    }
    const result: unknown = await response.json();
    if (
      !result ||
      typeof result !== 'object' ||
      Array.isArray(result) ||
      ('ok' in result && result.ok === false) ||
      ('error' in result && Boolean(result.error)) ||
      ('errors' in result &&
        Array.isArray(result.errors) &&
        result.errors.length > 0)
    ) {
      throw new Error(
        'Your signup could not be confirmed. Please check your email address and try again.',
      );
    }
  } finally {
    clearTimeout(timeout);
  }
}

export function EldgardWaitlist() {
  const [status, setStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [error, setError] = useState('');
  const submitting = useRef(false);

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current || status === 'success') return;
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const email = data.get('email');
    if (typeof email !== 'string') return;
    data.set('email', email.trim());
    submitting.current = true;
    setStatus('submitting');
    setError('');
    try {
      await submitWaitlist(data);
      form.reset();
      setStatus('success');
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message.startsWith('Too many requests')
          ? cause.message
          : 'Your signup could not be confirmed. Please try again or email hello@alxnsv.io.',
      );
      setStatus('error');
    } finally {
      submitting.current = false;
    }
  }

  return (
    <div className="eldgard-waitlist">
      <form
        action={waitlistEndpoint}
        method="POST"
        onSubmit={handleSubmit}
        hidden={status === 'success'}
        aria-label="Join the Eldgard waitlist"
        aria-busy={status === 'submitting'}
      >
        <label htmlFor="waitlist-email">Your email address</label>
        <div className="eldgard-waitlist-row">
          <input
            id="waitlist-email"
            type="email"
            name="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={254}
            required
            readOnly={status === 'submitting'}
            aria-describedby={
              status === 'error'
                ? 'waitlist-notice waitlist-error'
                : 'waitlist-notice'
            }
          />
          <button
            className="eldgard-button"
            type="submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Joining…' : 'Join the waitlist'}
            <ArrowRight size={17} aria-hidden="true" />
          </button>
        </div>
        <input
          className="eldgard-waitlist-trap"
          type="text"
          name="_gotcha"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <p id="waitlist-notice" className="eldgard-waitlist-notice">
          We’ll use your email for Eldgard early-access invitations and launch
          availability. No payment or account required. You can leave the list
          anytime by emailing us.{' '}
          <a href="/eldgard/privacy#privacy-use-sharing">Privacy policy</a>.
        </p>
        {status === 'error' && (
          <p id="waitlist-error" className="eldgard-waitlist-error" role="alert">
            {error}
          </p>
        )}
      </form>
      <output aria-live="polite" aria-atomic="true">
        {status === 'success' && (
          <span className="eldgard-waitlist-success">
            <Check size={24} aria-hidden="true" />
            <span>
              <strong>You’re on the list.</strong>
              <span className="eldgard-waitlist-success-message">
                We’ll email you when Eldgard early access opens.
              </span>
            </span>
          </span>
        )}
      </output>
    </div>
  );
}
